// `Toast` + inline `Banner` (design-system spec §3.8). Every mutation confirms
// visibly; the toast is never the sole record — the primary record is an
// inline Banner or a persistent state change on the screen itself.
//
// The bus is module-level so any screen can `toast.success(...)` without a
// provider ceremony; `<Toasts/>` is mounted once by the shell. `aria-live`
// polite, auto-dismiss after 5 s with hover-hold, at most 3 on the glass.

import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

export interface ToastItem {
  id: number;
  kind: "success" | "error";
  message: string;
}

type Listener = (items: readonly ToastItem[]) => void;

const MAX_TOASTS = 3;
export const TOAST_DISMISS_MS = 5_000;

let nextId = 1;
let items: readonly ToastItem[] = [];
const listeners = new Set<Listener>();

function publish(next: readonly ToastItem[]) {
  items = next;
  for (const l of listeners) l(items);
}

function push(kind: ToastItem["kind"], message: string) {
  publish([...items.slice(-(MAX_TOASTS - 1)), { id: nextId++, kind, message }]);
}

export const toast = {
  success: (message: string) => {
    push("success", message);
  },
  error: (message: string) => {
    push("error", message);
  },
};

export function dismissToast(id: number) {
  publish(items.filter((t) => t.id !== id));
}

/** Test hook: drop everything (also used on sign-out). */
export function clearToasts() {
  publish([]);
}

function ToastRow({ item }: { item: ToastItem }) {
  const [hover, setHover] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (hover) return undefined;
    timer.current = setTimeout(() => {
      dismissToast(item.id);
    }, TOAST_DISMISS_MS);
    return () => {
      clearTimeout(timer.current);
    };
  }, [hover, item.id]);

  return (
    <div
      className={`ds-toast${item.kind === "error" ? " ds-toast-error" : ""}`}
      data-testid={`toast-${item.kind}`}
      onMouseEnter={() => {
        setHover(true);
      }}
      onMouseLeave={() => {
        setHover(false);
      }}
    >
      <span>{item.message}</span>
      <button
        type="button"
        className="ds-banner-x"
        aria-label="✕"
        onClick={() => {
          dismissToast(item.id);
        }}
      >
        ✕
      </button>
    </div>
  );
}

export function Toasts() {
  const [current, setCurrent] = useState<readonly ToastItem[]>(items);
  useEffect(() => {
    listeners.add(setCurrent);
    setCurrent(items);
    return () => {
      listeners.delete(setCurrent);
    };
  }, []);
  return (
    <div className="ds-toasts" aria-live="polite" data-testid="toasts">
      {current.map((t) => (
        <ToastRow key={t.id} item={t} />
      ))}
    </div>
  );
}

/* ---------------- inline Banner — the primary record ---------------- */

export interface BannerProps {
  /** `warn` is the §1-rule-3 amber: needs attention, not yet failed (a routing
   * whose BOM references a step that no longer exists). Added at UX-2. */
  kind: "ok" | "warn" | "bad" | "neutral";
  children: ReactNode;
  /** Optional right-aligned chip (the "Setup +15% → 49%" delta). */
  aside?: ReactNode;
  onDismiss?: (() => void) | undefined;
  dismissLabel?: string;
  /** Extra class names, appended. Exists so a screen can keep a STABLE
   * WALKTHROUGH SELECTOR (`.bom-banner-draft`, read by certified M01-S07/S10)
   * while its markup moves onto this primitive — the alternative was leaving
   * hand-rolled banner divs in place purely to preserve a selector, which is
   * how one screen ended up rendering "this is read-only" three ways. */
  className?: string | undefined;
  "data-testid"?: string;
}

export function Banner({
  kind,
  children,
  aside,
  onDismiss,
  dismissLabel,
  className,
  ...rest
}: BannerProps) {
  const base =
    kind === "ok"
      ? "ds-banner ds-banner-ok"
      : kind === "warn"
        ? "ds-banner ds-banner-warn"
        : kind === "bad"
          ? "ds-banner ds-banner-bad"
          : "ds-banner";
  const cls = className !== undefined && className.length > 0 ? `${base} ${className}` : base;
  return (
    <div className={cls} role={kind === "bad" ? "alert" : "status"} {...rest}>
      {kind === "ok" ? (
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <circle cx="8" cy="8" r="6.2" />
          <path d="m5.2 8.2 2 2 3.6-4" />
        </svg>
      ) : null}
      <span>{children}</span>
      {aside !== undefined ? <span style={{ marginLeft: "auto" }}>{aside}</span> : null}
      {onDismiss !== undefined ? (
        <button
          type="button"
          className="ds-banner-x"
          aria-label={dismissLabel ?? "✕"}
          onClick={onDismiss}
        >
          ✕
        </button>
      ) : null}
    </div>
  );
}
